import {site} from "../../../context/context";
import {UIEvent} from "../../u-i-event";
import {TEvent, TEventSubtype} from "services-comun-meteored/modules/services/cmp/web/events";
import traduccion, {TraduccionCmpGdpr} from "cmp-web-translations/modules/traduccion/cmp/gdpr";
import {IIdioma} from "services-comun/modules/net/idiomas";
import AnalyticManager from "../../../analytic/analytic-manager";
import {EAction, ELabel} from "../../../analytic/event-definition";
import {publish} from "../../../util/js-event";
import {CMPWindow} from "../c-m-p-window";
import {ECMPWindow} from "../interface";
import {ApareceGlobal} from "../../../session/aparece-global";

interface ILangGDPR {
    title: string;
    data_p1: string;
    data_p2: string;
    data_p3: string;
    accept: string;
    reject: string;
    configure: string;
    num_vendors: string;
    query_vendors: string;
}

export enum EGDPREvent {
    ACEPTAR     = 'EV_GDPR_Aceptar',
    CONFIGURAR  = 'EV_GDPR_Configurar',
    RECHAZAR    = 'EV_GDPR_Rechazar',
    PRIVACIDAD  = 'EV_GDPR_Privacidad',
    VENDORS     = 'EV_GDPR_Vendors',
}

export interface IAcceptEvent {
}

export interface IConfigureEvent {
}

export interface IRejectEvent {
}

export interface IPrivacityEvent {
    url_privacidad: string;
}

export interface IVendorsEvent {
}

export class GDPRManager extends CMPWindow {
    /* STATIC */
    public static VAR_NAME_GDPR_TEMPLATE: string = 'gdpr_template';

    private static _INSTANCE: GDPRManager;

    public static instance(): GDPRManager {
        return this._INSTANCE ??= new GDPRManager();
    }

    /* INSTANCE */
    private _LANG: ILangGDPR|undefined;
    private _panel: HTMLElement|undefined;

    private constructor() {
        super(ECMPWindow.GDPR);
    }

    private get panel(): HTMLElement {
        if (!this._panel) {
            throw new Error('GDPR panel not found');
        }
        return this._panel;
    }

    public openGDPR(): void {
        this.buildGDPRPanel().then(async container => {
            this._panel = container;

            // Bloqueamos body
            document.body.classList.add('body_fixed');

            // Lanza el evento de apertura de GDPR
            const event = UIEvent.create(TEvent.APARECE_GDPR);
            event.register();
            event.notify();

            // Lanzamos evento analítico
            AnalyticManager().register({
                action: EAction.GDPR,
                label: ELabel.APARECE
            });

            if (ApareceGlobal.get().key !== '1') {
                AnalyticManager().register({
                    action: EAction.GDPR,
                    label: ELabel.APARECE_GLOBAL
                });
            }

            // Gestiona el panel de GDPR
            this.manageGDPRPanel();
        });
    }

    /**
     * Construye el panel principal del CMP para GDPR.
     * @private
     */
    private async buildGDPRPanel(): Promise<HTMLElement> {
        let html: string = (window as any)[GDPRManager.VAR_NAME_GDPR_TEMPLATE] || site.gdprTemplate() || '';

        if (!html) {
            const template = await import(/* webpackChunkName: "main-gdpr" */ '../../template/main-gdpr.pug' + '');
            const lang = this.loadI18N();

            import((`cmp-web-templates/scss/gdpr.scss`));
            html = template.default({
                brand: site.gdprParams()?.['brand'],
                logo: site.gdprParams()?.['logo'],
                logo_alt: site.gdprParams()?.['logo_alt'],
                cookies_policy: site.gdprParams()?.['cookies_policy'],
                reject_all: site.gdprParams()?.['cap_reject_all'],
                lang
            });
        } else {
            const lang = this.loadI18N();
            html = html
                .replace('[logo]', site.gdprParams()?.['logo'] || '')
                .replace('[logo_alt]', site.gdprParams()?.['logo_alt'] || '')
                .replace('[lang.title]', lang.title)
                .replace('[lang.data_p1]', lang.data_p1)
                .replace('[lang.data_p2]', lang.data_p2)
                .replace('[lang.data_p3]', lang.data_p3)
                .replace('[lang.accept]', lang.accept)
                .replace('[lang.reject]', lang.reject)
                .replace('[lang.configure]', lang.configure)
            ;
        }

        const element: HTMLDivElement = document.createElement('div');
        element.innerHTML = html;

        const mainContainer: HTMLElement = <HTMLElement>element.firstElementChild;

        if (site.gdprCSS()) {
            document.body.insertAdjacentHTML('beforeend', `<style>${site.gdprCSS()}</style>`);
        }
        document.body.insertAdjacentElement('beforeend', mainContainer);

        return mainContainer;
    }

    /**
     * Gestiona la ventana de GDPR.
     * @private
     */
    private manageGDPRPanel() : void {
        // Panel
        const panel = this.panel;

        // Click sobre el elemento vendors
        const vendors: HTMLElement = <HTMLElement>panel.querySelector('#link-vendors');
        if (vendors) {
            this.addLinkVendorsListener(vendors);
        }

        // Resto de elementos que abren la lista de vendors
        Array.from(panel.querySelectorAll('.link-vendors')).forEach(element => {
            this.addLinkVendorsListener(<HTMLElement>element);
        });

        // Click sobre el elemento privacity
        const privacity: HTMLElement = <HTMLElement>panel.querySelector('#url-policy');
        if (privacity) {
            privacity.onclick = () => {
                publish<IPrivacityEvent>(EGDPREvent.PRIVACIDAD, {
                    url_privacidad: privacity.dataset['link'] || ''
                });
            };
        }

        // Botón de aceptar
        const accept: HTMLElement = <HTMLElement>panel.querySelector('#btn-gdpr-accept');
        if (accept) {
            accept.onclick = async () => {
                publish<IAcceptEvent>(EGDPREvent.ACEPTAR, {});
            };
        }

        // Botón de configurar
        const configure: HTMLElement = <HTMLElement>panel.querySelector('#btn-gdpr-configure');
        if (configure) {
            configure.onclick = () => {
                publish<IConfigureEvent>(EGDPREvent.CONFIGURAR, {});
            };
        }

        // Botón de rechazar
        const reject: HTMLElement = <HTMLElement>panel.querySelector('#btn-gdpr-reject');
        if (reject) {
            reject.onclick = async () => {
                publish<IRejectEvent>(EGDPREvent.RECHAZAR, {})
            };
        }
    }

    private addLinkVendorsListener(element: HTMLElement): void {
        element.onclick = () => {

            publish<IVendorsEvent>(EGDPREvent.VENDORS, {});
        };
    }

    private loadI18N(): ILangGDPR {
        if (!this._LANG) {
            const idioma: IIdioma = {
                idioma: site.language,
                idioma_corto: site.language.split('-')[0],
                defecto: 'en',
                defecto_corto: 'en'
            };
            const lang = traduccion(idioma);
            const [
                title,
                data_p1,
                data_p2,
                data_p3,
                accept,
                reject,
                configure,
                num_vendors,
                query_vendors
            ] = [
                lang.title,
                lang.data_p1(site.gdprParams()?.['num_socios'] === "1" ? 'socios' : 'no_socios', {brand: site.gdprParams()?.['brand'], num_vendors: `${site.numVendors()}`}),
                lang.data_p2({brand: site.gdprParams()?.['brand']}),
                lang.data_p3(site.langKey(), {
                    url_cookies: site.gdprParams()?.['cookies_policy'],
                    url_privacidad: site.gdprParams()?.['cookies_policy']
                }),
                this.getLangValue<TraduccionCmpGdpr>(lang, lang.accept, site.gdprParams()?.['lang.accept']),
                this.getLangValue<TraduccionCmpGdpr>(lang, lang.reject, site.gdprParams()?.['lang.reject']),
                this.getLangValue<TraduccionCmpGdpr>(lang, lang.configure, site.gdprParams()?.['lang.configure']),
                lang.n_vendors({
                    num_vendors: `${site.numVendors()}`
                }),
                lang.query_vendors
            ];
            this._LANG = {
                title,
                data_p1,
                data_p2,
                data_p3,
                accept,
                reject,
                configure,
                num_vendors,
                query_vendors
            }
        }
        return this._LANG;
    }

    public override close(): void {
        // Cierra la ventana de GDPR
        this.panel.parentElement?.removeChild(this.panel);

        // Liberamos body
        document.body.classList.remove('body_fixed');

        // Lanzamos el evento de cierre
        const event = UIEvent.create(TEvent.CIERRA_CMP, TEventSubtype.FIRST_LAYER);
        event.register();
        event.notify();
    }
}

